import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Heart, Users, ArrowRight } from "lucide-react";
import imagehero from "../../../images/ImageforHeroSection.jpeg";
import KUhomepage from "../../../images/KU_homepage.jpg";
import KJposter from "../../../images/KJposter.jpeg";

const slides = [
  {
    id: "volunteering",
    image: imagehero,
    eyebrow: "Join the Cause",
    title: "Find Yourself Through\nVolunteering",
    description: "Balancing self-interest with the common good — discover the profound joy of giving your time, skills, and heart.",
    ctas: [
      { label: "Volunteer Now", to: "/volunteering", icon: Users, primary: true },
      { label: "Donate Now", to: "/donate", icon: Heart, gold: true },
    ],
    badge: "Youth-Led Movement",
  },
  {
    id: "connect",
    image: KUhomepage,
    eyebrow: "Kindness Unlimited",
    title: "Connecting India Through\nthe Web of Kindness",
    description: "A youth-centric, volunteer-led NGO spreading compassion and humanity across communities in India.",
    ctas: [
      { label: "Volunteer Now", to: "/volunteering", icon: Users, primary: true },
      { label: "Donate Now", to: "/donate", icon: Heart, gold: true },
    ],
    badge: "Make a Difference",
  },
  {
    id: "impact",
    image: KJposter,
    eyebrow: "Our Impact",
    title: "Kindness is\na Boomerang",
    description: "Over 2.2 lakh givers, 56 lakh receivers, and 82 lakh observers — kindness multiplies when shared.",
    ctas: [
      { label: "Impact Reports", to: "/impact", icon: ArrowRight, primary: false },
      { label: "Donate Now", to: "/donate", icon: Heart, gold: true },
    ],
    badge: "8.2M+ Lives Touched",
  },
  {
    id: "campaigns",
    image: KUhomepage,
    eyebrow: "Active Campaigns",
    title: "Spreading Kindness\nOne Act at a Time",
    description: "From Daan Utsav to Kindness Jam — every campaign is a thread in India's growing web of kindness.",
    ctas: [
      { label: "View Campaigns", to: "/project-kul", icon: ArrowRight, primary: true },
      { label: "Donate Now", to: "/donate", icon: Heart, gold: true },
    ],
    badge: "20+ Active Years",
  },
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 8000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className={`relative min-h-[90vh] lg:min-h-screen w-full overflow-hidden ${
      current === 2 ? "bg-ku-purple-deep" : "bg-white"
    }`}>
      {/* Background Images (cross-fade) */}
      <AnimatePresence mode="sync">
        {slides.map((s, i) => (
          <motion.div
            key={s.id}
            className="absolute inset-0"
            style={{ zIndex: i === current ? 1 : 0 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: i === current ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <img
              src={s.image}
              alt={s.title}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Overlay - conditional based on slide */}
      <div className="absolute inset-0 z-[2]" style={{
        background: current === 2
          ? "linear-gradient(105deg, rgba(30,10,50,0.85) 0%, rgba(50,20,80,0.8) 40%, rgba(70,30,110,0.75) 70%, rgba(100,50,150,0.65) 100%)"
          : "linear-gradient(105deg, rgba(255,255,255,0.5) 0%, rgba(240,230,255,0.4) 40%, rgba(230,245,255,0.3) 70%, rgba(255,245,230,0.3) 100%)"
      }} />

      {/* Decorative blobs - soft pastels */}
      <div className="absolute top-1/4 right-10 w-72 h-72 rounded-full bg-ku-gold/12 blur-3xl z-[2] animate-float-delayed pointer-events-none" />
      <div className="absolute bottom-1/4 left-20 w-48 h-48 rounded-full bg-ku-purple/8 blur-2xl z-[2] animate-float pointer-events-none" />
      <div className="absolute bottom-10 right-1/3 w-36 h-36 rounded-full bg-ku-violet/10 blur-3xl z-[2] animate-float pointer-events-none" />

      {/* Content */}
      <div className="relative z-[3] min-h-[90vh] lg:min-h-screen flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-24">
        <div className="max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-accent text-xs font-semibold tracking-wide uppercase mb-5 backdrop-blur-sm border ${
                  current === 2
                    ? "bg-white/15 border-white/20 text-white/90"
                    : "bg-ku-purple/10 border-ku-purple/20 text-ku-purple"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-ku-gold animate-pulse" />
                {slide.badge}
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.7 }}
                className={`font-display font-bold mb-5 leading-[1.08] ${
                  current === 2 ? "text-white" : "text-ku-purple"
                }`}
                style={{ fontSize: "clamp(2.4rem, 5vw, 4.5rem)" }}
              >
                {slide.title.split("\n").map((line, i) => (
                  <span key={i} className={i === 1 ? "text-ku-gold block" : "block"}>
                    {line}
                  </span>
                ))}
              </motion.h1>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className={`font-body text-lg leading-relaxed mb-8 max-w-2xl ${
                  current === 2 ? "text-white" : "text-black"
                }`}
              >
                {slide.description}
              </motion.p>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="flex flex-wrap gap-3"
              >
                {slide.ctas.map((cta) => (
                  <Link
                    key={cta.label}
                    to={cta.to}
                    className={
                      current === 2
                        ? cta.gold
                          ? "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-white bg-ku-gold shadow-elevated hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                          : cta.primary
                            ? "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-white bg-white/20 border border-white/40 backdrop-blur-sm hover:bg-white/30 hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                            : "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-white bg-white/15 border border-white/30 backdrop-blur-sm hover:bg-white/25 hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                        : cta.gold
                          ? "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-white bg-ku-gold shadow-elevated hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                          : cta.primary
                            ? "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-white bg-ku-purple shadow-elevated hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                            : "inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-accent font-semibold text-ku-purple bg-ku-soft border border-ku-soft/60 hover:bg-ku-cream hover:scale-[1.03] active:scale-[0.97] transition-all duration-200 text-sm"
                    }
                  >
                    <cta.icon className="w-4 h-4" fill={cta.icon === Heart ? "currentColor" : "none"} />
                    {cta.label}
                  </Link>
                ))}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide Controls */}
        <div className="absolute bottom-10 left-6 sm:left-10 lg:left-16 xl:left-24 right-6 sm:right-10 flex items-center justify-between gap-4">
          {/* Dots */}
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-400 ${
                  i === current
                    ? current === 2 ? "w-8 h-2 bg-ku-gold" : "w-8 h-2 bg-ku-purple"
                    : current === 2 ? "w-2 h-2 bg-white/40 hover:bg-white/70" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          {/* Prev / Next */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                current === 2
                  ? "bg-white/10 border border-white/20 text-white hover:bg-white/20"
                  : "bg-ku-soft border border-ku-soft/60 text-ku-purple hover:bg-ku-cream"
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next"
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                current === 2
                  ? "bg-white/10 border border-white/20 text-white hover:bg-white/20"
                  : "bg-ku-soft border border-ku-soft/60 text-ku-purple hover:bg-ku-cream"
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className={`absolute bottom-0 left-0 right-0 h-0.5 z-10 ${
          current === 2 ? "bg-white/10" : "bg-gray-200"
        }`}>
          <motion.div
            key={current}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 8, ease: "linear" }}
            className={`h-full origin-left ${
              current === 2 ? "bg-ku-gold" : "bg-ku-purple"
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
